import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Activity, Clock3, FolderKanban, Package, ShieldAlert, Truck, Wrench } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { Panel, PanelHeader } from '@/components/app/States';
import { ExampleDataNote } from '@/components/app/DriftUI';
import { DRIFT_REPORTS } from '@/lib/driftApi';
import { useDriftFilters } from '@/components/app/DriftFilters';

/*
  Rapporter – én enkel inngang. Produksjonsrapporten (produksjon, diesel, CO₂) er uendret og ligger først.
  Driftsrapportene bygger på registreringene i Timer/Vareforbruk/Maskinpark og eksporteres alltid som filtrert utvalg.
*/

const ICONS = { timer: Clock3, prosjektforbruk: FolderKanban, vareforbruk: Package, maskiner: Truck, hendelser: Wrench, avvik: ShieldAlert };

function ReportLink({ to, icon: Icon, title, description, testId, meta }) {
  return (
    <li>
      <Link to={to} className="group/rep flex items-start gap-4 px-5 py-4 transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" data-testid={testId}>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[var(--hr-inset)] text-[var(--hr-muted)] transition-colors duration-150 group-hover/rep:bg-white group-hover/rep:text-[var(--hr-heading)]">
          <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold text-[var(--hr-heading)]">{title}</span>
            {meta && <span className="rounded-full bg-[var(--hr-surface-2)] px-1.5 py-px text-[12px] font-medium text-[var(--hr-muted)]">{meta}</span>}
          </div>
          <p className="mt-0.5 text-[14px] leading-5 text-[var(--hr-muted)]">{description}</p>
        </div>
        <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-[var(--hr-muted-2)] transition-colors duration-150 group-hover/rep:text-[var(--hr-heading)]" aria-hidden="true" />
      </Link>
    </li>
  );
}

export default function RapporterPage() {
  const f = useDriftFilters({ keys: [] });
  React.useEffect(() => {
    document.title = 'Rapporter · Hardanger Rock';
  }, []);
  return (
    <div className="space-y-5">
      <PageHeader title="Rapporter" lead="Velg rapport. Periode og prosjekt velges inne i rapporten; eksport gir alltid det filtrerte utvalget." />

      <Panel>
        <PanelHeader title="Produksjon" description="Produsert tonn, diesel og CO₂ fra produksjonsgrunnlaget." />
        <ul>
          <ReportLink to={`/rapporter/produksjon${f.search}`} icon={Activity} title="Perioderapport – produksjon, diesel og CO₂" description="Nøkkeltall, sammenligning, tre funn, datadekning og avgrensning. Utskrift/PDF og CSV." testId="report-link-produksjon" />
        </ul>
      </Panel>

      <Panel>
        <PanelHeader title="Drift" description="Bygger på registreringene i Timer, Vareforbruk og Maskinpark. CSV, Excel (XLSX) og utskrift/PDF." />
        <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="drift-report-links">
          {DRIFT_REPORTS.map((r) => (
            <ReportLink key={r.id} to={`/rapporter/${r.id}${f.carry({})}`} icon={ICONS[r.id]} title={r.label} description={r.description} testId={`report-link-${r.id}`} meta="Eksempeldata" />
          ))}
        </ul>
      </Panel>

      <ExampleDataNote>Driftsrapportene viser tid og mengder – ingen inntekter, satser eller fakturagrunnlag. Forbrukte tonn er forbruk, ikke produserte tonn.</ExampleDataNote>
    </div>
  );
}
